import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import BigNumber from 'bignumber.js'
import { getFarmsConfig } from 'config/farms'
import { useWagmi } from 'libs/wagmi'
import { useAppDispatch } from 'state'
import { fetchFarmsPublicDataAsync, fetchFarmUserDataAsync } from 'state/farms'
import { fetchPoolsPublicDataAsync, fetchPoolsUserDataAsync } from 'state/pools'
import { SerializedFarm, SerializedPool, State } from 'state/types'
import { BIG_ZERO } from 'config/constants/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'

const getFarmedInUsd = (farm: SerializedFarm) =>
  getBalanceAmount(new BigNumber(farm.lpTokenPriceBusd).multipliedBy(farm.userData?.stakedBalance || 0))

const getStakedInUsd = (pool: SerializedPool) =>
  getBalanceAmount(
    new BigNumber(pool.userData?.stakedBalance || 0).multipliedBy(pool.stakingTokenPrice),
    pool.stakingToken.decimals,
  )

export const useTotalInvestedUsd = () => {
  const dispatch = useAppDispatch()
  const { account, chainId } = useWagmi()

  useEffect(() => {
    const farms = getFarmsConfig(chainId)

    dispatch(fetchFarmsPublicDataAsync({ chainId, pids: [], farms }))
    dispatch(fetchPoolsPublicDataAsync(chainId))

    if (account) {
      dispatch(fetchFarmUserDataAsync({ account, chainId, pids: [], farms }))
      dispatch(fetchPoolsUserDataAsync(account, chainId))
    }
  }, [dispatch, account, chainId])

  const farms = useSelector((state: State) => state.farms.data.filter(({ pid }) => pid && pid > 0))
  const pools = useSelector((state: State) => state.pools.data)
  const userDataLoaded = useSelector((state: State) => state.farms.userDataLoaded && state.pools.userDataLoaded)

  const totalUsdInAllFarms = userDataLoaded
    ? farms.reduce((accum, farm) => accum.plus(getFarmedInUsd(farm)), BIG_ZERO).toNumber()
    : 0

  const totalUsdInAllPools = userDataLoaded
    ? pools.reduce((accum, pool) => accum.plus(getStakedInUsd(pool)), BIG_ZERO).toNumber()
    : 0

  return {
    totalUsdInAllFarms,
    totalUsdInAllPools,
    userDataLoaded,
  }
}
